import React, { useState } from 'react';
import axios from 'axios';
import { ShieldCheck, User, Phone, Loader2 } from 'lucide-react';
import { API_URL } from '../../utils/apiConfig';

/**
 * Mandatory profile completion for social sign-ups (Google/Apple).
 * Captures the user's REAL legal name (as on their ID) — used to match their
 * identity document during verification — plus an optional phone number.
 * Cannot be dismissed without submitting a valid first + last name.
 */
const ProfileCompletionModal = ({ auth, onComplete }) => {
  const user = auth?.user || {};
  const [firstName, setFirstName] = useState(user.first_name || '');
  const [middleName, setMiddleName] = useState(user.middle_name || '');
  const [lastName, setLastName] = useState(user.last_name || '');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name exactly as they appear on your ID.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(
        `${API_URL}/auth/complete-profile`,
        {
          first_name: firstName.trim(),
          middle_name: middleName.trim() || null,
          last_name: lastName.trim(),
          phone: phone.trim() || null,
        },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      if (res.data?.success) {
        onComplete({ token: res.data.token, user: res.data.user });
      } else {
        setError(res.data?.detail || 'Could not save your details. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save your details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-6 bg-black/60 backdrop-blur-sm overflow-y-auto"
      data-testid="profile-completion-modal"
    >
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 my-auto">
        <div className="flex flex-col items-center text-center mb-5">
          <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mb-3">
            <User className="w-7 h-7 text-emerald-600" strokeWidth={2.4} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Complete your profile</h2>
          <p className="text-sm text-gray-600 mt-1">
            Please enter your details to finish setting up your account.
          </p>
        </div>

        {/* Verification notice */}
        <div className="flex items-start gap-2.5 bg-emerald-50 border border-emerald-200 rounded-2xl p-3 mb-5">
          <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-emerald-900 leading-relaxed">
            Enter your name exactly as it appears on your ID. Your name will be used to
            match with your ID for verification.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700" data-testid="profile-completion-error">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">First Name (as on ID) *</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => { setFirstName(e.target.value); setError(''); }}
              placeholder="Enter first name"
              data-testid="profile-first-name"
              className="w-full h-12 px-4 rounded-xl bg-white border border-gray-300 text-gray-900 placeholder-gray-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Middle Name (optional)</label>
            <input
              type="text"
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value)}
              placeholder="Enter middle name"
              data-testid="profile-middle-name"
              className="w-full h-12 px-4 rounded-xl bg-white border border-gray-300 text-gray-900 placeholder-gray-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Last Name (as on ID) *</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => { setLastName(e.target.value); setError(''); }}
              placeholder="Enter last name"
              data-testid="profile-last-name"
              className="w-full h-12 px-4 rounded-xl bg-white border border-gray-300 text-gray-900 placeholder-gray-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Phone Number <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d+\s]/g, ''))}
                placeholder="e.g. 0991 234 567"
                data-testid="profile-phone"
                className="w-full h-12 pl-12 pr-4 rounded-xl bg-white border border-gray-300 text-gray-900 placeholder-gray-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">You can add or change this later in Settings.</p>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="profile-completion-submit"
            className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-base font-bold shadow-lg shadow-emerald-500/25 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Saving…</>) : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfileCompletionModal;
